import { AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Cloudflare Turnstile tokens for the anonymous backend calls that cost money or create records.
 *
 * On 23-25/10/2026 a script replayed the onboarding OTP call ~10,700 times (SMS pumping, ~2,300 EUR).
 * The backend (`RestWorld.BotChallenge` in core-services) now asks those calls for a token that only a
 * browser running the Turnstile widget on one of our pages can mint. This module mints it and the Axios
 * interceptor in `axios.ts` attaches it, so no call site has to know about it.
 *
 * - The token travels in the `x-bot-challenge` header, minted for the action the backend expects on that
 *   endpoint (`BOT_CHALLENGE_ENDPOINTS`). A token is single-use: one per request.
 * - Without `NEXT_PUBLIC_TURNSTILE_SITE_KEY`, or on the server (SSR), nothing happens.
 * - The Cloudflare script is loaded on the first protected call, never on page load.
 * - The widgets are Managed: most visitors pass without seeing anything (`appearance: 'interaction-only'`),
 *   a visitor Cloudflare is unsure about gets a checkbox. When that happens the silent widget is dropped and the
 *   checkbox is shown in `BotChallengeHost` (ui-components), mounted once per app: this module only publishes
 *   the request (`subscribeBotChallengeInteraction`), so it stays free of React.
 * - Deadlines: `TOKEN_TIMEOUT_MS` while silent, `INTERACTIVE_TIMEOUT_MS` once a person has a checkbox in front
 *   of them. Without a token the request still goes, carrying `none:<reason>` instead (timeout, abandoned,
 *   error, unavailable) so the backend logs why: while the action is in `log` mode it lets it through. Never
 *   blocking a person on our side is deliberate; the backend decides.
 * - Every outcome is published (`subscribeBotChallengeOutcome`) for the host to send to analytics.
 * - A 403 with `code: "bot_challenge_failed"` (action in `enforce`) is retried once with a fresh token.
 */

export const BOT_CHALLENGE_HEADER = 'x-bot-challenge';
const FAILED_CODE = 'bot_challenge_failed';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 8000;
const INTERACTIVE_TIMEOUT_MS = 90000;

/** Method + path of each protected endpoint, and the action name the backend checks the token against. */
export const BOT_CHALLENGE_ENDPOINTS: ReadonlyArray<{ method: string; path: string; action: string }> = [
  { method: 'get', path: '/accounts/verification_phone_otp', action: 'otp_request' },
  { method: 'post', path: '/accounts/verify_phone_otp', action: 'otp_verify' },
  { method: 'post', path: '/workers/upload_cv', action: 'cv_upload' },
  { method: 'post', path: '/workers/cv', action: 'cv_missing_data' },
  { method: 'post', path: '/workers/worker', action: 'worker_create' },
  { method: 'post', path: '/organizations', action: 'organization_create' },
  { method: 'put', path: '/organizations/prospects', action: 'prospect_upsert' },
  { method: 'post', path: '/employers/job_positions/generate_ad_content', action: 'ad_generate' },
  { method: 'post', path: '/employers/job_positions/validate_ad', action: 'ad_validate' },
  { method: 'get', path: '/employers/restaurants/additional_information', action: 'venue_information' },
];

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  language?: string;
  callback: (token: string) => void;
  'error-callback': () => boolean | void;
  'timeout-callback': () => void;
  'expired-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'refresh-expired': 'never';
  retry: 'never';
};

type Turnstile = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const siteKey = () => process.env['NEXT_PUBLIC_TURNSTILE_SITE_KEY'];

/** Whether this page can mint tokens at all. */
export const isBotChallengeEnabled = () => typeof window !== 'undefined' && !!siteKey();

const pathOf = (url: string | undefined) => {
  if (!url) return '';
  try {
    return new URL(url, 'http://relative.invalid').pathname.replace(/\/+$/, '');
  } catch {
    return url.split('?')[0].replace(/\/+$/, '');
  }
};

/** The action a request needs a token for, or undefined when the endpoint is not protected. */
export const botChallengeActionFor = (method: string | undefined, url: string | undefined) => {
  const path = pathOf(url);
  return BOT_CHALLENGE_ENDPOINTS.find(
    (endpoint) => endpoint.method === (method || 'get').toLowerCase() && endpoint.path === path
  )?.action;
};

let scriptPromise: Promise<Turnstile | undefined> | undefined;

/** Loads the Cloudflare script once. Also used by `useBotChallenge` to warm it up on the funnels. */
export const loadTurnstile = (): Promise<Turnstile | undefined> => {
  if (!isBotChallengeEnabled()) return Promise.resolve(undefined);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    // Iubenda autoblocking: purpose 1 = strictly necessary, so the consent banner never holds it back.
    script.setAttribute('data-iub-purposes', '1');
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      scriptPromise = undefined;
      resolve(undefined);
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

/** Why a request went without a token, as the backend logs it. */
export type BotChallengeFailure = 'timeout' | 'abandoned' | 'error' | 'unavailable';

export type BotChallengeOutcome = {
  action: string;
  outcome: 'passed' | 'passed_interactive' | BotChallengeFailure;
  elapsedMs: number;
};

/** A checkbox waiting to be shown: the host calls `mount` with a visible element, `cancel` when it is closed. */
export type BotChallengeInteraction = {
  action: string;
  mount: (container: HTMLElement) => void;
  cancel: () => void;
};

type MintResult = { token: string; interactive: boolean } | { failure: BotChallengeFailure };

const interactionListeners = new Set<(interaction: BotChallengeInteraction | null) => void>();
const outcomeListeners = new Set<(outcome: BotChallengeOutcome) => void>();
// One checkbox at a time: two protected calls can start together (prospects and venue information do).
const interactionQueue: BotChallengeInteraction[] = [];

const publishInteraction = () => interactionListeners.forEach((listener) => listener(interactionQueue[0] ?? null));

/** The host listens here; it receives the checkbox to show, or null when there is none. */
export const subscribeBotChallengeInteraction = (listener: (interaction: BotChallengeInteraction | null) => void) => {
  interactionListeners.add(listener);
  listener(interactionQueue[0] ?? null);
  return () => {
    interactionListeners.delete(listener);
  };
};

export const subscribeBotChallengeOutcome = (listener: (outcome: BotChallengeOutcome) => void) => {
  outcomeListeners.add(listener);
  return () => {
    outcomeListeners.delete(listener);
  };
};

const mintToken = (action: string): Promise<MintResult> =>
  new Promise<MintResult>((resolve) => {
    const startedAt = Date.now();
    let settled = false;
    let widgetId: string | undefined;
    let container: HTMLElement | undefined;
    let interaction: BotChallengeInteraction | undefined;
    let timer = setTimeout(() => finish({ failure: 'timeout' }), TOKEN_TIMEOUT_MS);

    const finish = (result: MintResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (widgetId) window.turnstile?.remove(widgetId);
      container?.remove();
      if (interaction) {
        const index = interactionQueue.indexOf(interaction);
        if (index >= 0) interactionQueue.splice(index, 1);
        publishInteraction();
      }
      const outcome = 'token' in result ? (result.interactive ? 'passed_interactive' : 'passed') : result.failure;
      outcomeListeners.forEach((listener) => listener({ action, outcome, elapsedMs: Date.now() - startedAt }));
      resolve(result);
    };

    const handlers = (interactive: boolean) => ({
      callback: (token: string) => finish({ token, interactive }),
      'error-callback': () => {
        finish({ failure: 'error' });
        return true;
      },
      'timeout-callback': () => finish({ failure: 'timeout' }),
      'expired-callback': () => finish({ failure: 'timeout' }),
      'refresh-expired': 'never' as const,
      retry: 'never' as const,
    });

    // A widget can answer inside render(), before its id is known: remove it then, or remember it for finish().
    // The silent widget can also go interactive inside render(): then it is already replaced and goes too.
    const keep = (turnstile: Turnstile, id: string | undefined, silent: boolean) => {
      if (settled || (silent && interaction)) {
        if (id) turnstile.remove(id);
      } else widgetId = id;
    };

    // Cloudflare wants a click: drop the silent widget and hand a visible one to the host, if there is a host.
    const goInteractive = (turnstile: Turnstile, sitekey: string) => {
      if (settled || interaction || interactionListeners.size === 0) return;
      clearTimeout(timer);
      timer = setTimeout(() => finish({ failure: 'timeout' }), INTERACTIVE_TIMEOUT_MS);
      if (widgetId) turnstile.remove(widgetId);
      widgetId = undefined;
      container?.remove();
      container = undefined;
      interaction = {
        action,
        mount: (visible) => {
          if (settled || widgetId) return;
          keep(turnstile, turnstile.render(visible, { sitekey, action, ...handlers(true) }), false);
        },
        cancel: () => finish({ failure: 'abandoned' }),
      };
      interactionQueue.push(interaction);
      publishInteraction();
    };

    loadTurnstile().then((turnstile) => {
      const sitekey = siteKey();
      if (settled) return;
      if (!turnstile || !sitekey) return finish({ failure: 'unavailable' });

      // Off screen, not display:none: an interaction-only widget stays invisible until it needs a person.
      container = document.createElement('div');
      container.setAttribute('aria-hidden', 'true');
      container.style.cssText = 'position:fixed;left:-10000px;top:0;width:300px;height:65px;';
      document.body.appendChild(container);
      keep(
        turnstile,
        turnstile.render(container, {
          sitekey,
          action,
          appearance: 'interaction-only',
          'before-interactive-callback': () => goInteractive(turnstile, sitekey),
          ...handlers(false),
        }),
        true
      );
    });
  });

/**
 * Mints one token for `action`, or resolves undefined when it cannot (no key, SSR, widget failure, timeout, the
 * person closed the checkbox).
 */
export const getBotChallengeToken = async (action: string): Promise<string | undefined> => {
  if (!isBotChallengeEnabled()) return undefined;
  const result = await mintToken(action);
  return 'token' in result ? result.token : undefined;
};

type RetriableConfig = InternalAxiosRequestConfig & { botChallengeRetried?: boolean };

const attachToken = async (config: InternalAxiosRequestConfig, action: string) => {
  const result = await mintToken(action);
  // No token: say why, so the backend can tell a person whose widget failed from a script that never ran one.
  const value = 'token' in result ? result.token : `none:${result.failure}`;
  if (config.headers) (config.headers as AxiosHeaders).set(BOT_CHALLENGE_HEADER, value);
  return config;
};

/** Request interceptor: a fresh token on every protected call. */
export const botChallengeRequestInterceptor = async (config: InternalAxiosRequestConfig) => {
  if (!isBotChallengeEnabled()) return config;
  const action = botChallengeActionFor(config.method, config.url);
  return action ? attachToken(config, action) : config;
};

/**
 * Response error handler: when the backend refused the token (expired, already spent, widget glitch), replay
 * the request once with a new one. Returns undefined when the error is not ours to handle.
 */
export const retryOnBotChallengeFailure = async (instance: AxiosInstance, error: AxiosError) => {
  const config = error.config as RetriableConfig | undefined;
  const data = error.response?.data as { code?: string } | undefined;
  if (!config || error.response?.status !== 403 || data?.code !== FAILED_CODE || config.botChallengeRetried)
    return undefined;

  const action = botChallengeActionFor(config.method, config.url);
  if (!action || !isBotChallengeEnabled()) return undefined;

  // The request interceptor mints the fresh token when the request goes through the instance again.
  config.botChallengeRetried = true;
  (config.headers as AxiosHeaders | undefined)?.delete?.(BOT_CHALLENGE_HEADER);
  return instance.request(config);
};
